import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UserV2 } from '../../../types/dashboardV2';
import { ROUTES } from '../../../constants/routes';
import {
  getMobileUserStatusBadgeClass,
  getMobileUserStatusDotClass,
  formatUserStatus,
  formatUserRole,
  getRoleBadgeClass,
} from '../../../shared/utils/statusUtils';
import { formatDate } from '../../../shared/utils/dateFormat';
import { MobileStat } from '../components/MobileCardParts';

interface MobileUserCardProps {
  user: UserV2;
}

// rerender-memo: Memoize list item component to prevent unnecessary re-renders
export const MobileUserCard = memo(function MobileUserCard({
  user,
}: MobileUserCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleClick = useCallback(
    () => navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_USERS}/${user.id}`),
    [navigate, user.id],
  );

  // Display email if available, otherwise phone
  const contactInfo = user.email || user.phone || '-';
  const contactLabel = user.email
    ? t('dashboard.mobileCards.email')
    : user.phone
      ? t('dashboard.mobileCards.phone')
      : t('dashboard.mobileCards.contact');
  const contactIcon = user.email ? 'mail' : user.phone ? 'phone' : 'person';

  return (
    <div
      className="mobile-station-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={t('dashboard.mobileCards.userAria', { contact: contactInfo })}
    >
      {/* Header with Contact and Status */}
      <div className="mobile-card-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="mobile-card-id-label">
            <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', marginRight: '0.25rem', verticalAlign: 'middle' }} aria-hidden="true">{contactIcon}</span>
            {contactLabel}
          </span>
          <h3 className="mobile-card-id-value" style={{ fontSize: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {contactInfo}
          </h3>
        </div>
        <span className={`mobile-status-badge ${getMobileUserStatusBadgeClass(user.status)}`}>
          <span className={`mobile-status-dot ${getMobileUserStatusDotClass(user.status)}`} />
          {formatUserStatus(user.status)}
        </span>
      </div>

      {/* Stats Grid - 2x2 */}
      <div className="mobile-card-stats mobile-card-stats-2x2">
        <MobileStat
          icon="account_balance_wallet"
          label={t('dashboard.mobileCards.balanceLabel')}
          value={`${user.balance.toFixed(2)} ₴`}
        />
        <MobileStat
          icon="badge"
          label={t('dashboard.mobileCards.roleLabel')}
          value={
            <span className={`role-badge-mobile ${getRoleBadgeClass(user.role)}`}>
              {formatUserRole(user.role)}
            </span>
          }
        />
        <MobileStat
          icon="calendar_today"
          label={t('dashboard.mobileCards.createdLabel')}
          value={formatDate(user.createdAt)}
        />
        <MobileStat
          icon="update"
          label={t('dashboard.mobileCards.updatedLabel')}
          value={formatDate(user.updatedAt)}
        />
      </div>
    </div>
  );
});

export default MobileUserCard;
